/**
 * Script-aware text helpers.
 *
 * Latin text carries two cues these helpers would otherwise assume: spaces mark word
 * boundaries, and one character is one column wide. Neither holds for Chinese, so any
 * layout maths written against Latin quietly misbehaves — labels never wrap, initials
 * collapse to one glyph, widths come out at half the truth.
 */

/** CJK ideographs plus the kana blocks, which break between any two characters. */
const UNSPACED_SCRIPT = /[぀-ヿ㐀-䶿一-鿿豈-﫿]/;

/**
 * Whether `text` contains any character from a script that does not separate words
 * with spaces. Labels are routinely mixed (`ILP 投資相連壽險`), so callers must treat
 * this as "contains", never as "is entirely".
 */
export function isUnspacedScript(text: string): boolean {
  return UNSPACED_SCRIPT.test(text);
}

/**
 * Split into the smallest units a line may break between: each unspaced-script
 * character stands alone, while a run of Latin stays whole so wrapping can never
 * cut a word in half.
 */
function breakUnits(text: string): string[] {
  const units: string[] = [];
  let latin = '';
  for (const ch of text) {
    if (UNSPACED_SCRIPT.test(ch)) {
      if (latin) {
        units.push(latin);
        latin = '';
      }
      units.push(ch);
    } else if (/\s/.test(ch)) {
      if (latin) {
        units.push(latin);
        latin = '';
      }
    } else {
      latin += ch;
    }
  }
  if (latin) units.push(latin);
  return units;
}

/**
 * Rough display width in "Latin character" units. A Han glyph occupies about two
 * columns in the monospace-ish grids that chart axes and spreadsheet cells assume.
 */
export function displayWidth(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += isUnspacedScript(ch) ? 2 : 1;
  }
  return width;
}

/**
 * Break `text` into lines no wider than `maxWidth` display columns.
 *
 * One pass over break units handles Latin, Chinese and the mixed labels the product
 * actually produces (`ILP 投資相連壽險`): Latin words stay whole, Chinese breaks
 * anywhere. A line still overflows when a single word is wider than `maxWidth` —
 * splitting a word is worse than one long line.
 */
export function wrapByDisplayWidth(text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let current = '';

  for (const unit of breakUnits(text.trim())) {
    if (!current) {
      current = unit;
      continue;
    }
    // Latin units were separated by whitespace; Chinese ones butt together.
    const separator =
      UNSPACED_SCRIPT.test(unit) || UNSPACED_SCRIPT.test(current.slice(-1)) ? '' : ' ';
    const candidate = `${current}${separator}${unit}`;
    if (displayWidth(candidate) <= maxWidth) current = candidate;
    else {
      lines.push(current);
      current = unit;
    }
  }

  if (current) lines.push(current);
  return lines;
}

/**
 * Cut `text` to `maxWidth` display columns, appending an ellipsis when anything was
 * dropped. Counting columns rather than characters keeps a Chinese label inside the
 * same box as an English one of equivalent visual length.
 */
export function truncateToDisplayWidth(text: string, maxWidth: number): string {
  if (displayWidth(text) <= maxWidth) return text;

  // Reserve one column for the ellipsis itself.
  const budget = Math.max(1, maxWidth - 1);
  let width = 0;
  let out = '';
  for (const ch of text) {
    const next = width + (isUnspacedScript(ch) ? 2 : 1);
    if (next > budget) break;
    out += ch;
    width = next;
  }
  return `${out}…`;
}

/**
 * Two initials for an avatar placeholder.
 *
 * First and last word, not the first two: `Nguyễn Văn An` gives `NA`, keeping the
 * given name that Vietnamese and Chinese names put last — taking words 1-2 would
 * yield the surname and middle name and drop the part people are called by.
 *
 * Whitespace decides the rule, not the script (`Peter 陳` → `P陳`). An unspaced
 * Chinese name has no word boundary at all, so convention takes its leading two
 * characters rather than the single glyph a Latin rule would return.
 */
export function getInitials(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '';

  const words = trimmed.split(/\s+/);
  if (words.length === 1) {
    return isUnspacedScript(trimmed)
      ? Array.from(trimmed).slice(0, 2).join('')
      : (Array.from(trimmed)[0]?.toUpperCase() ?? '');
  }

  return [words[0], words[words.length - 1]]
    .map((word) => Array.from(word)[0]?.toUpperCase() ?? '')
    .join('');
}
